/**
 * studio/src/lib/auth.ts
 *
 * Pinia auth store — follows the Vue + Pinia pattern from the Appwrite docs.
 * Handles:
 *   - Magic link send
 *   - Session creation from secret (callback URL)
 *   - Route guarding
 *   - Creator record get-or-create on login
 */

import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { account, getCurrentUser, sendMagicLink, createSessionFromSecret, deleteCurrentSession } from "./appwrite";
import type { Models } from "appwrite";

export const useAuthStore = defineStore("auth", () => {
  // ─── State ──────────────────────────────────────────────────────────────
  // `Models.User` is generic in the v16 SDK — `<Preferences>` defaults
  // to `DefaultPreferences`, but TS requires us to spell it out when
  // we declare the ref. We don't read any user preferences in the
  // studio today, so `Record<string, unknown>` is the honest type
  // for the shape we actually carry.
  const user = ref<Models.User<Record<string, unknown>> | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);

  // ─── Getters ───────────────────────────────────────────────────────────
  const isLoggedIn = computed(() => !!user.value);
  const userId = computed(() => user.value?.$id ?? null);
  const userEmail = computed(() => user.value?.email ?? null);

  // ─── Actions ──────────────────────────────────────────────────────────

  /** Call on app mount — checks for existing session */
  async function init() {
    if (initialized.value) return;
    initialized.value = true;
    loading.value = true;
    error.value = null;
    try {
      user.value = await getCurrentUser();
    } catch {
      user.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Send a magic link to the given email.
   * @param email  Recipient email
   * @param redirectUrl  Where the magic link lands — must include the current origin
   *                    e.g. `${window.location.origin}/auth/callback`
   */
  async function requestMagicLink(email: string, redirectUrl: string) {
    loading.value = true;
    error.value = null;
    try {
      await sendMagicLink(email, redirectUrl);
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : "Failed to send magic link";
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Complete login from the magic-link callback URL.
   * Call from /auth/callback with ?userId=...&secret=... query params.
   */
  async function completeMagicLogin(userId: string, secret: string) {
    loading.value = true;
    error.value = null;
    try {
      await createSessionFromSecret(userId, secret);
      user.value = await getCurrentUser();
    } catch (err: unknown) {
      error.value = err instanceof Error ? err.message : "Login failed";
      user.value = null;
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    loading.value = true;
    error.value = null;
    try {
      await deleteCurrentSession();
    } catch (err: unknown) {
      // Session may already be gone — clear state regardless
    } finally {
      user.value = null;
      loading.value = false;
    }
  }

  return {
    // state
    user,
    loading,
    error,
    initialized,
    // getters
    isLoggedIn,
    userId,
    userEmail,
    // actions
    init,
    requestMagicLink,
    completeMagicLogin,
    logout,
  };
});
